/**
 * Breeder Enquiries Inbox
 * Manage incoming enquiries from buyers, respond, schedule viewings
 */

import { useState, useEffect } from 'react';
import { 
  MessageSquare, User, Phone, Mail, Clock, CheckCircle, Calendar, Send,
  Eye, Trash2, ChevronRight, Filter, Search, Bell, Star, Archive
} from 'lucide-react';
import { Card, Button, Badge, Modal, Input, Select, Alert, Tabs } from '../shared/UIComponents';
import { formatCurrency, formatDate, formatRelativeDate, cn } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { collection, addDoc, updateDoc, doc, query, where, getDocs, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';

const ENQUIRY_STATUS = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700' },
  replied: { label: 'Replied', color: 'bg-paw-100 text-paw-700' },
  viewing_scheduled: { label: 'Viewing Scheduled', color: 'bg-purple-100 text-purple-700' },
  converted: { label: 'Converted', color: 'bg-amber-100 text-amber-700' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-500' }
};

export const BreederEnquiries = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enquiries, setEnquiries] = useState([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activeTab, setActiveTab] = useState('new');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user?.catteryId) loadEnquiries();
  }, [user?.catteryId]);

  const loadEnquiries = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'enquiries'), where('catteryId', '==', user.catteryId), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setEnquiries(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading enquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const newEnquiries = enquiries.filter(e => e.status === 'new' || e.hasNewMessage);
  const activeEnquiries = enquiries.filter(e => ['replied', 'viewing_scheduled'].includes(e.status));
  const closedEnquiries = enquiries.filter(e => ['converted', 'closed'].includes(e.status));

  const filterEnquiries = (list) => {
    if (!searchTerm) return list;
    const term = searchTerm.toLowerCase();
    return list.filter(e => e.buyerName?.toLowerCase().includes(term) || e.catName?.toLowerCase().includes(term));
  };

  const getFilteredList = () => {
    switch (activeTab) {
      case 'new': return filterEnquiries(newEnquiries);
      case 'active': return filterEnquiries(activeEnquiries);
      case 'closed': return filterEnquiries(closedEnquiries);
      default: return filterEnquiries(enquiries);
    }
  };

  const tabs = [
    { id: 'new', label: 'New', count: newEnquiries.length },
    { id: 'active', label: 'Active', count: activeEnquiries.length },
    { id: 'closed', label: 'Closed', count: closedEnquiries.length },
    { id: 'all', label: 'All' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">Enquiries</h1>
          <p className="text-gray-500">Manage buyer enquiries and conversations</p>
        </div>
        {newEnquiries.length > 0 && (
          <Badge variant="primary">{newEnquiries.length} new</Badge>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <Bell className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">New</p>
            <p className="text-2xl font-bold text-gray-900">{newEnquiries.length}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-bold text-gray-900">{activeEnquiries.length}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
            <Star className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Converted</p>
            <p className="text-2xl font-bold text-gray-900">{enquiries.filter(e => e.status === 'converted').length}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 bg-paw-100 rounded-xl flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-paw-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Response Rate</p>
            <p className="text-2xl font-bold text-gray-900">{enquiries.length > 0 ? Math.round((enquiries.filter(e => e.status !== 'new').length / enquiries.length) * 100) : 0}%</p>
          </div>
        </Card>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl" />
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      <EnquiriesList enquiries={getFilteredList()} loading={loading} onSelect={(e) => { setSelectedEnquiry(e); setShowDetailModal(true); }} onStatusChange={async (id, status) => { await updateDoc(doc(db, 'enquiries', id), { status, updatedAt: serverTimestamp() }); loadEnquiries(); }} />

      <EnquiryDetailModal isOpen={showDetailModal} onClose={() => { setShowDetailModal(false); setSelectedEnquiry(null); }} enquiry={selectedEnquiry} onRefresh={loadEnquiries} />
    </div>
  );
};

const EnquiriesList = ({ enquiries, loading, onSelect, onStatusChange }) => {
  if (loading) return <Card><div className="animate-pulse space-y-4">{[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-lg" />)}</div></Card>;
  if (enquiries.length === 0) return <Card className="text-center py-12"><MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">No Enquiries</h3></Card>;

  return (
    <Card className="overflow-hidden p-0">
      <div className="divide-y divide-gray-200">
        {enquiries.map((enquiry) => {
          const statusConfig = ENQUIRY_STATUS[enquiry.status] || ENQUIRY_STATUS.new;
          return (
            <div key={enquiry.id} className={cn("p-4 hover:bg-gray-50 cursor-pointer flex items-center gap-4", (enquiry.status === 'new' || enquiry.hasNewMessage) && "bg-blue-50")} onClick={() => onSelect(enquiry)}>
              <div className="w-14 h-14 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                {enquiry.catPhoto ? <img src={enquiry.catPhoto} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-2xl">🐱</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-gray-900">{enquiry.buyerName}</p>
                  {enquiry.hasNewMessage && <Badge variant="primary" className="text-xs">New</Badge>}
                </div>
                <p className="text-sm text-gray-600 truncate">Re: <span className="font-medium">{enquiry.catName}</span></p>
                <p className="text-xs text-gray-500 mt-1">{formatRelativeDate(enquiry.createdAt)}</p>
              </div>
              <select value={enquiry.status} onChange={(e) => { e.stopPropagation(); onStatusChange(enquiry.id, e.target.value); }} onClick={(e) => e.stopPropagation()} className={cn('text-xs font-medium px-3 py-1.5 rounded-full border-0', statusConfig.color)}>
                <option value="new">New</option>
                <option value="replied">Replied</option>
                <option value="viewing_scheduled">Viewing</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
              </select>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const EnquiryDetailModal = ({ isOpen, onClose, enquiry, onRefresh }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [viewingDate, setViewingDate] = useState('');

  useEffect(() => {
    if (enquiry?.id) {
      loadMessages();
      if (enquiry.hasNewMessage) updateDoc(doc(db, 'enquiries', enquiry.id), { hasNewMessage: false });
    }
  }, [enquiry?.id]);

  const loadMessages = async () => {
    const q = query(collection(db, 'enquiries', enquiry.id, 'messages'), orderBy('createdAt', 'asc'));
    const snap = await getDocs(q);
    setMessages(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      setSending(true);
      await addDoc(collection(db, 'enquiries', enquiry.id, 'messages'), { text: newMessage, senderId: user.uid, senderName: 'Breeder', senderType: 'breeder', createdAt: serverTimestamp() });
      await updateDoc(doc(db, 'enquiries', enquiry.id), { status: enquiry.status === 'new' ? 'replied' : enquiry.status, lastMessage: newMessage, hasNewReply: true, updatedAt: serverTimestamp() });
      setNewMessage('');
      loadMessages();
      onRefresh();
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSending(false);
    }
  };

  const scheduleViewing = async () => {
    if (!viewingDate) return;
    await updateDoc(doc(db, 'enquiries', enquiry.id), { status: 'viewing_scheduled', viewingDate, updatedAt: serverTimestamp() });
    await addDoc(collection(db, 'enquiries', enquiry.id, 'messages'), { text: `Viewing scheduled for ${new Date(viewingDate).toLocaleString('en-ZA')}. Looking forward to meeting you!`, senderId: user.uid, senderName: 'Breeder', senderType: 'breeder', createdAt: serverTimestamp() });
    setViewingDate('');
    loadMessages();
    onRefresh();
  };

  if (!enquiry) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Enquiry Details" size="lg">
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                {enquiry.catPhoto ? <img src={enquiry.catPhoto} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-2xl">🐱</div>}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{enquiry.catName}</p>
                {enquiry.catPrice && <p className="text-feline-600 font-bold">{formatCurrency(enquiry.catPrice)}</p>}
              </div>
            </div>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="font-semibold text-gray-900 mb-2">{enquiry.buyerName}</p>
            {enquiry.buyerPhone && <a href={`tel:${enquiry.buyerPhone}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-feline-600"><Phone className="w-4 h-4" />{enquiry.buyerPhone}</a>}
            {enquiry.buyerEmail && <a href={`mailto:${enquiry.buyerEmail}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-feline-600"><Mail className="w-4 h-4" />{enquiry.buyerEmail}</a>}
          </div>
        </div>

        <div className="p-4 bg-blue-50 rounded-xl">
          <p className="text-sm text-blue-600 mb-1">Original Enquiry • {formatDate(enquiry.createdAt)}</p>
          <p className="text-gray-800">{enquiry.message}</p>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <a href={`tel:${enquiry.buyerPhone}`} className="flex items-center justify-center gap-2 p-3 bg-gray-100 hover:bg-gray-200 rounded-xl"><Phone className="w-5 h-5" /><span className="text-sm font-medium">Call</span></a>
          <a href={`https://wa.me/${(enquiry.buyerPhone || '').replace(/\D/g, '')}`} target="_blank" className="flex items-center justify-center gap-2 p-3 bg-paw-100 hover:bg-paw-200 rounded-xl"><MessageSquare className="w-5 h-5 text-paw-600" /><span className="text-sm font-medium text-paw-700">WhatsApp</span></a>
          <button onClick={() => window.location.href = `/dashboard/sales?new=true&buyerName=${encodeURIComponent(enquiry.buyerName)}`} className="flex items-center justify-center gap-2 p-3 bg-amber-100 hover:bg-amber-200 rounded-xl"><Star className="w-5 h-5 text-amber-600" /><span className="text-sm font-medium text-amber-700">Convert</span></button>
        </div>

        {enquiry.status !== 'viewing_scheduled' && (
          <div className="flex gap-2">
            <Input type="datetime-local" value={viewingDate} onChange={(e) => setViewingDate(e.target.value)} className="flex-1" />
            <Button onClick={scheduleViewing} disabled={!viewingDate}><Calendar className="w-4 h-4" />Schedule</Button>
          </div>
        )}

        {enquiry.viewingDate && <Alert type="info"><Calendar className="w-4 h-4 inline mr-2" />Viewing: {new Date(enquiry.viewingDate).toLocaleString('en-ZA')}</Alert>}

        <div className="border-t pt-4">
          <h4 className="font-semibold text-gray-900 mb-3">Conversation</h4>
          <div className="max-h-64 overflow-y-auto space-y-3 mb-4">
            {messages.length === 0 ? <p className="text-gray-500 text-center py-4">No messages yet</p> : messages.map((msg) => (
              <div key={msg.id} className={cn('p-3 rounded-xl max-w-[80%]', msg.senderType === 'breeder' ? 'bg-feline-100 ml-auto' : 'bg-gray-100')}>
                <p className="text-sm text-gray-800">{msg.text}</p>
                <p className="text-xs text-gray-500 mt-1">{msg.senderName} • {formatRelativeDate(msg.createdAt)}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Type reply..." className="flex-1" onKeyPress={(e) => e.key === 'Enter' && sendMessage()} />
            <Button onClick={sendMessage} loading={sending}><Send className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BreederEnquiries;
